import React, { useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import Header from "../app/Header.js";
import { Container, Row, Col, Form, Card, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHistory, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { NAMESPACE_REMOTE } from "../../common/translations/TranslationHelper";
import PreviousOrderHelper from "../../common/helpers/PreviousOrderHelper.js";
import { useToken } from "../../common/hooks/useToken.js";
import { TOKEN_KEY_FIRM_ID } from "../../common/helpers/TokenHelper";
import axios from "../../common/api/HttpClient";
import { useLang } from "../../common/hooks/useLang";

export default function Home() {
  const navigate = useNavigate();
  const { currentLanguage } = useLang();

  const [previousProductKey] = useState(() => {
    return PreviousOrderHelper.getCachedProductKey();
  });

  const [previousOrder] = useState(() => {
    return PreviousOrderHelper.getCachedOrder();
  });

  const [cachedOrderFirmId, setCachedOrderFirmId] = useState(null);
  useEffect(() => {
    let notInterrupted = true;

    if (previousOrder != null) {
      const standardOrderId = previousOrder.AUFTRAG.standardauftragId;
      axios({
        method: "get",
        url: `/default-order/${standardOrderId}`,
        params: { sprache: currentLanguage }
      })
        .then((response) => {
          if (notInterrupted) {
            setCachedOrderFirmId(response.data.KUNDE.firmaId);
          }
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            console.log("Standardauftrag nicht gefunden oder für Kunde nicht sichtbar.");
          } else {
            console.error(error);
          }
        });
    }

    // If clean-up function executes, the axios call was interrupted
    // and the results can not be rendered in the dom anymore.
    // This may happen, if the user navigates back during the call.
    return () => {
      notInterrupted = false;
    }
  }, [previousOrder, currentLanguage]);

  const [disablePreviousAnalyses, setDisablePreviousAnalyses] = useState(true);
  const { accessToken } = useToken();
  useEffect(() => {
    if (previousProductKey === null) {
      setDisablePreviousAnalyses(true);
    }
    else {
      const userFirmId = accessToken.decodedToken[TOKEN_KEY_FIRM_ID];
      // Preset only, if FIRM_ID matches with the logged in user,
      // because the last order may be created by another logged in user
      if (previousOrder && cachedOrderFirmId && (cachedOrderFirmId === "0" || cachedOrderFirmId === userFirmId)) {
        setDisablePreviousAnalyses(false);
      } else {
        setDisablePreviousAnalyses(true);
      }
    }
  }, [previousProductKey, previousOrder, accessToken.decodedToken, cachedOrderFirmId]);
  
  const handleCreateAnalysis = () => {
    navigate("/products", { replace: true });
  };

  const handleCreateFromPreviousAnalyses = () => {
    if (previousProductKey !== null) {
      const searchParams = createSearchParams({
        id: previousProductKey,
        preset: "true"
      });
      navigate({
        pathname: "/order",
        search: `?${searchParams}`,
      });
    }
  };

  const { t } = useTranslation(NAMESPACE_REMOTE);

  return (
    <Container className="p-0" fluid>
      <header>
        <Header title={t("PROMPT.WSHOP2.HOME.TITLE")}></Header>
      </header>

      <main>
        <Container fluid="md" className="pt-3">
          <Form>
            <Form.Group as={Row}>
              <Col xs={12} sm={12}>
                <h1 className="h2">{t("TEXT.WSHOP2.HOME.HEADLINE_1")}</h1>
                <p>{t("TEXT.WSHOP2.HOME.HEADLINE_2")}</p>
              </Col>
            </Form.Group>

            <Form.Group>
              <Row xs={1} md={2} className="g-4">
                <Col>
                  <Card className="h-100">
                    <Card.Body className="d-flex flex-column">
                      <Card.Title>{t("TEXT.WSHOP2.HOME.NEW_ORDER")}</Card.Title>
                      <Card.Text className="flex-grow-1">{t("TEXT.WSHOP2.HOME.NEW_ORDER_HINT")}</Card.Text>
                      <div>
                        <Button
                          variant="primary"
                          className="text-white"
                          onClick={handleCreateAnalysis}
                        >
                          <FontAwesomeIcon icon={faPlus} />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col>
                  <Card className="h-100">
                    <Card.Body className="d-flex flex-column">
                      <Card.Title>{t("TEXT.WSHOP2.HOME.NEW_ORDER_WITH_PRESET")}</Card.Title>
                      <Card.Text className="flex-grow-1">{t("TEXT.WSHOP2.HOME.NEW_ORDER_WITH_PRESET_HINT")}</Card.Text>
                      <div>
                        <Button
                          variant="primary"
                          className="text-white"
                          onClick={handleCreateFromPreviousAnalyses}
                          disabled={disablePreviousAnalyses}
                        >
                          <FontAwesomeIcon icon={faHistory} />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Form.Group>
          </Form>
        </Container>
      </main>
    </Container>
  );
}
